<template>
    <v-container fluid>
        <v-row dense>
            <v-col v-if="!this.$store.state.auth.user.cargoTypeID" class="pb-0">
                <CargoTypeSelector v-model="cargoTypeID" dense :error-messages="cargoTypeErrors"
                    @input="onFilterChanged" :can-any="true" />
            </v-col>
            <v-col class="pb-0">
                <ClaimStatusSelector v-model="claimStatusID" dense :can-any="true" @input="onFilterChanged" />
            </v-col>
            <v-col class="pb-0">
                <v-text-field v-model="numberContains" dense append-icon="mdi-magnify" label="№ заявки содержит"
                    single-line hide-details autocomplete="off" class="pb-4" @input="lazyQuery(onFilterChanged)" />
            </v-col>
            <v-col class="pb-0">
                <PeriodSelector v-model="period" dense :error-messages="periodErrors" @input="touchPeriod"
                    @blur="touchPeriod" />
            </v-col>
        </v-row>

        <v-data-table dense :items="items" :headers="headers" :loading="loading" :server-items-length="rows"
            hide-default-footer disable-pagination disable-filtering disable-sort item-key="requestId"
            loading-text="Загрузка данных..." no-data-text="Нет заявок, соответствующих фильтру..."
            no-results-text="Совпадений не найдено..." class="styled-table pointed-table" @click:row="requestClick">
            <template v-slot:[`item.requestDate`]="{ item }">
                {{ formatDate(item.requestDate) }}
            </template>
            <template v-slot:[`item.claimsCount`]="{ item }">
                <v-chip small color="primary" v-if="item.claimsCount">
                    {{ item.claimsCount }}
                </v-chip>
            </template>
            <template v-slot:[`item.approvedSum`]="{ item }">
                {{ item.approvedSum ? Number(item.approvedSum).toFixed(2) : '0.00' }}
            </template>
        </v-data-table>
    </v-container>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import PeriodSelector from '@/components/PeriodSelector.vue';
import CargoTypeSelector from '@/components/CargoTypeSelector.vue';
import ClaimStatusSelector from '@/components/ClaimStatusSelector.vue';

export default {
    name: 'IncidentRecords',

    mixins: [validationMixin],

    components: {
        PeriodSelector,
        CargoTypeSelector,
        ClaimStatusSelector
    },

    data() {
        return {
            cargoTypeID: null,
            numberContains: '',
            period: {
                dateFrom: null,
                dateTo: null
            },
            requestStatusID: 0,
            claimStatusID: null,

            headers: [
                { text: "Тип", value: "claimTypeName", class: "tableHeader tableHeaderText--text" },
                { text: "№ заявки", value: "requestNumber", class: "tableHeader tableHeaderText--text" },
                { text: "Дата", value: "requestDate", class: "tableHeader tableHeaderText--text" },
                { text: "№ машины", value: "carNumber", class: "tableHeader tableHeaderText--text" },
                { text: "Водитель", value: "driverName", class: "tableHeader tableHeaderText--text" },
                { text: "Претензий", value: "claimsCount", class: "tableHeader tableHeaderText--text" },
                { text: "Сумма одобренного", value: "approvedSum", class: "tableHeader tableHeaderText--text" },
            ],
            items: [],
            loading: false,
            rows: 0,
            queryTimeout: null
        };
    },

    validations: {
        cargoTypeID: {
            required
        },
        period: {
            dateFrom: {
                required
            },
            dateTo: {
                required
            }
        }
    },

    computed: {
        cargoTypeErrors() {
            const errors = [];
            if (!this.$v.cargoTypeID.$dirty) return errors;
            if (!this.$v.cargoTypeID.required) {
                errors.push("Задайте тип груза");
            }
            return errors;
        },

        periodErrors() {
            const errors = [];
            if (!this.$v.period.dateFrom.$dirty && !this.$v.period.dateTo.$dirty) {
                return errors;
            }
            if (!this.$v.period.dateFrom.required) {
                errors.push("Задайте начальную дату");
            }
            if (!this.$v.period.dateTo.required) {
                errors.push("Задайте конечную дату");
            }
            return errors;
        },

        // Получаем разрешенных пользователей из роутера
        allowedUsers() {
            const route = this.$router.options.routes.find(r => r.path === '/incident-records');
            return route?.meta?.allowedUsers || [];
        },

        isUserAllowed() {
            const user = this.$store.state.auth.user;
            if (!user) return false;
            const userId = parseInt(user.userID);
            return this.allowedUsers.includes(userId);
        }
    },

    async created() {
        if (!this.isUserAllowed) {
            this.$router.push('/');
            return;
        }

        if (!this.$store.getters['claimsStatuses/loaded']) {
            await this.$store.dispatch('claimsStatuses/init');
        }

        if (!this.$store.state.auth.user.cargoTypeID) {
            this.headers.splice(0, 0, {
                text: "Тип груза",
                value: "cargoTypeName",
                class: "tableHeader tableHeaderText--text"
            });
        }

        await this.loadClaims();
    },

    beforeDestroy() {
        if (this.queryTimeout) {
            clearTimeout(this.queryTimeout);
        }
    },

    methods: {
        touchPeriod() {
            this.$v.period.dateFrom.$touch();
            this.$v.period.dateTo.$touch();
            this.onFilterChanged();
        },

        onFilterChanged() {
            this.loadClaims();
        },

        lazyQuery(callback) {
            if (this.queryTimeout) {
                clearTimeout(this.queryTimeout);
            }
            this.queryTimeout = setTimeout(() => {
                if (callback && typeof callback === 'function') {
                    callback();
                }
            }, 300);
        },

        requestClick(item) {
            if (item && item.requestId) {
                this.$router.push(`/incident-records/${item.requestId}`);
            }
        },

        async loadClaims() {
            if (!this.isUserAllowed) {
                return;
            }

            try {
                this.loading = true;

                const params = {
                    cargoTypeId: this.cargoTypeID
                };

                if (this.numberContains && this.numberContains.trim()) {
                    params.orderNumberSearchPart = this.numberContains.trim();
                }

                if (this.period.dateFrom) {
                    params.dateFrom = this.formatDateForApi(this.period.dateFrom);
                }
                if (this.period.dateTo) {
                    params.dateTo = this.formatDateForApi(this.period.dateTo);
                }

                if (this.requestStatusID && this.requestStatusID !== 0) {
                    params.requestStatusId = this.requestStatusID;
                }

                if (this.claimStatusID !== null && this.claimStatusID !== undefined) {
                    params.claimStatusId = this.claimStatusID;
                }

                const response = await this.axios.get('/requests/RequestClaims', { params });
                this.items = response.data;
                this.rows = response.data.length;

            } catch (error) {
                console.error('Ошибка при загрузке претензий:', error);
                this.$toast.error('Ошибка при загрузке данных');
                this.items = [];
                this.rows = 0;
            } finally {
                this.loading = false;
            }
        },

        formatDate(dateString) {
            if (!dateString) return '';

            if (dateString instanceof Date) {
                if (isNaN(dateString.getTime())) return '';
                return dateString.toLocaleDateString('ru-RU');
            }

            if (typeof dateString === 'string') {
                if (!dateString.trim() || dateString === 'null' || dateString === 'undefined') {
                    return '';
                }

                if (dateString.includes('T')) {
                    const date = new Date(dateString);
                    if (!isNaN(date.getTime())) {
                        return date.toLocaleDateString('ru-RU');
                    }
                }

                const parts = dateString.split('.');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);

                    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                        const date = new Date(year, month, day);
                        if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('ru-RU');
                        }
                    }
                }

                try {
                    const date = new Date(dateString);
                    if (!isNaN(date.getTime())) {
                        return date.toLocaleDateString('ru-RU');
                    }
                } catch (e) {}

                return '';
            }

            return '';
        },

        formatDateForApi(date) {
            if (!date) return null;

            if (date instanceof Date) {
                return date.toISOString();
            }

            if (typeof date === 'string') {
                const parts = date.split('.');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    const dateObj = new Date(year, month, day);
                    if (!isNaN(dateObj.getTime())) {
                        return dateObj.toISOString();
                    }
                }

                const dateObj = new Date(date);
                if (!isNaN(dateObj.getTime())) {
                    return dateObj.toISOString();
                }
            }

            if (typeof date === 'number') {
                const dateObj = new Date(date);
                if (!isNaN(dateObj.getTime())) {
                    return dateObj.toISOString();
                }
            }

            return null;
        }
    }
};
</script>

<style scoped>
.pointed-table>>>tbody tr {
    cursor: pointer;
}

.pointed-table>>>tbody tr:hover {
    background-color: #f5f5f5;
}
</style>